// SETTINGS V2 — nameplate enable confirm (B7-FE1, plan §6A.3 affordance 1).
//
// Not a plain text confirm, deliberately. One thing MUST be on screen before the
// plate goes public: the EXACT photo that will be exposed — a standing Security
// requirement (B7_SECURITY_REVIEW.md, Confirmations/S5), not scope creep. When the
// machine has no explicit primary image and no catalog silhouette the server sends
// null, and we show a "no photo" placeholder — that is the truthful preview, not a
// fallback to some other photo.
//
// NO public/anonymous document exposure — owner ruling 2026-08-13 (D14): documents
// are login-only. Do not re-add a visibility tick or a public docs list without a
// new owner ruling. The former second block — "N document(s) marked public will be
// readable", the re-exposure guard — is removed with the feature it guarded: no
// document is public any more, so the count is always zero and the line is noise.
//
// Disable uses the plain SettingsV2ConfirmDialog — only enable needs the preview.
// Inline styles + scoped CSS-var tokens. No MUI, no dt tokens, no Tailwind.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import type { NameplateFleetItem } from "../../../machinery/models/nameplateModels";
import SettingsV2DialogShell from "./SettingsV2DialogShell";

export interface NameplateEnableDialogV2Props {
  open: boolean;
  target: NameplateFleetItem | null;
  onClose: () => void;
  onConfirm: () => void;
  busy?: boolean;
}

const NameplateEnableDialogV2: React.FC<NameplateEnableDialogV2Props> = ({
  open,
  target,
  onClose,
  onConfirm,
  busy,
}) => {
  const { translate: t } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");

  if (!target) return null;

  const displayName = `${target.manufacturerName || ""} ${target.model || ""}`.trim();

  const footer = (
    <>
      <button type="button" className="btn btn-ghost" onClick={onClose}>
        {tCommon("buttons.cancel")}
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onConfirm}
        disabled={busy}
      >
        {t("settings:nameplates.enableConfirmAction")}
      </button>
    </>
  );

  return (
    <SettingsV2DialogShell
      open={open}
      onClose={onClose}
      width={460}
      title={t("settings:nameplates.enableConfirmTitle")}
      sub={displayName}
      labelledById="sv2-nameplate-enable-title"
      footer={footer}
    >
      <p style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.6, margin: 0 }}>
        {t("settings:nameplates.enableConfirmBody")}
      </p>

      {/* The exact public photo (Security S5). */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: 12,
          background: "var(--card-2)",
          border: "1px solid var(--border-soft)",
          borderRadius: 10,
          minWidth: 0,
        }}
      >
        <div
          style={{
            width: 76,
            height: 76,
            borderRadius: 8,
            flexShrink: 0,
            overflow: "hidden",
            display: "grid",
            placeItems: "center",
            background: "var(--bg-soft)",
            border: "1px solid var(--border)",
            color: "var(--faint)",
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            textAlign: "center",
            padding: 6,
            boxSizing: "border-box",
          }}
        >
          {target.thumbnailUrl ? (
            <img
              src={target.thumbnailUrl}
              alt={t("settings:nameplates.photoPreviewLabel")}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            t("settings:nameplates.noPhoto")
          )}
        </div>
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9.5,
              textTransform: "uppercase",
              letterSpacing: "0.09em",
              color: "var(--faint)",
            }}
          >
            {t("settings:nameplates.photoPreviewLabel")}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.55 }}>
            {target.thumbnailUrl
              ? t("settings:nameplates.photoPreviewHint")
              : t("settings:nameplates.noPhotoHint")}
          </div>
        </div>
      </div>
    </SettingsV2DialogShell>
  );
};

export default NameplateEnableDialogV2;
